import { attr, belongsTo } from "@ember-data/model";
import dayjs from "dayjs";
import {
  validateDate,
  validateInclusion,
  validateNumber,
  validatePresence
} from "ember-changeset-validations/validators";
import type IntlService from "ember-intl/services/intl";
import JournalEvent from "parog-web/models/journal-event";
import type Patient from "parog-web/models/patient";
import tWithDefault from "parog-web/utils/t-with-default";

//---- Automatically generated, don't update this part ----

type MomentOfTheDayStringEnum =
  | "morning"
  | "noon"
  | "afternoon"
  | "evening"
  | "night";

type MeasurementFastingStringEnum =
  | "emptyStomach"
  | "beforeMeal"
  | "afterMeal"
  | "other";

type InsulinStringEnum = "yes" | "no" | "dontKnow";

export default class CapillarityBloodGlucoseMeasuredJournalEvent extends JournalEvent {
  static type = "capillarity-blood-glucose-measured-journal-event";

  @attr("string") declare momentOfTheDay: MomentOfTheDayStringEnum;

  @attr("string") declare measurementFasting: MeasurementFastingStringEnum;

  /**
   * Stored in mmol
   */
  @attr("number") declare capillarityBloodGlucose: number;

  @attr("string") declare insulin: InsulinStringEnum;

  @attr("number") declare slowInsulin: number;

  @attr("number") declare fastInsulin: number;

  static validations(intl: IntlService) {
    return {
      capillarityBloodGlucose: [
        validatePresence({
          presence: true,
          description: intl.t(
            "validations.capillarity-blood-glucose-measured-journal-event.capillarity-blood-glucose"
          )
        }),
        validateNumber({
          gt: 0,
          description: intl.t(
            "validations.capillarity-blood-glucose-measured-journal-event.capillarity-blood-glucose"
          )
        })
      ],
      measurementFasting: [
        validatePresence({
          presence: true,
          description: intl.t(
            "validations.capillarity-blood-glucose-measured-journal-event.measurement-fasting"
          )
        }),
        validateInclusion({
          list: ["emptyStomach", "beforeMeal", "afterMeal", "other"],
          description: intl.t(
            "validations.capillarity-blood-glucose-measured-journal-event.measurement-fasting"
          )
        })
      ],
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.capillarity-blood-glucose-measured-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date",
          onOrBefore: dayjs().endOf("day").toDate()
        })
      ],
      slowInsulin: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.capillarity-blood-glucose-measured-journal-event.slow-insulin"
        )
      }),
      fastInsulin: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.capillarity-blood-glucose-measured-journal-event.fast-insulin"
        )
      }),
      momentOfTheDay: validateInclusion({
        list: ["morning", "noon", "afternoon", "evening", "night"],
        allowBlank: true,
        description: intl.t(
          "validations.capillarity-blood-glucose-measured-journal-event.moment-of-the-day"
        )
      }),
      insulin: validateInclusion({
        list: ["yes", "no", "dontKnow"],
        allowBlank: true,
        description: intl.t(
          "validations.capillarity-blood-glucose-measured-journal-event.insulin"
        )
      })
    };
  }

  //---- End of generated code ----
  @belongsTo("patient", {
    async: false,
    inverse: "capillarityBloodGlucoseMeasuredJournalEvents"
  })
  declare patient: Patient;

  static convertToGram(valueInMmol: number | undefined): number {
    return valueInMmol === undefined ? 0 : valueInMmol * 0.1801;
  }

  static convertToMmol(valueInGram: number | undefined): number {
    return valueInGram === undefined ? 0 : valueInGram / 0.1801;
  }

  get capillarityBloodGlucoseInGram(): number {
    return CapillarityBloodGlucoseMeasuredJournalEvent.convertToGram(
      this.capillarityBloodGlucose
    );
  }

  get prePrandialCapillarityBloodGlucoseInGram(): number | undefined {
    return this.measurementFasting === "emptyStomach" ||
      this.measurementFasting === "beforeMeal"
      ? this.capillarityBloodGlucoseInGram
      : undefined;
  }

  get postPrandialCapillarityBloodGlucoseInGram(): number | undefined {
    return this.measurementFasting === "afterMeal" ||
      this.measurementFasting === "other"
      ? this.capillarityBloodGlucoseInGram
      : undefined;
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "capillarity-blood-glucose-measured-journal-event": CapillarityBloodGlucoseMeasuredJournalEvent;
  }
}
